/** @jsxRuntime automatic */
/**
 * The captcha in front of a bespoke site's contact form.
 *
 * THE PRAGMA ON LINE 1 IS LOAD-BEARING, and it is not stylistic. This file is
 * compiled by more than one toolchain now, and they do not agree on the JSX
 * runtime:
 *
 *   - Vite and `tsc` resolve the NEAREST tsconfig, get `"jsx": "react-jsx"`,
 *     and emit the automatic runtime. Correct.
 *   - `tsx` (esbuild) applies ONE tsconfig — the one at its cwd — to every file
 *     it loads, including files from other packages. `@gjl/cms-components`'
 *     `field-efficacy-sweep` runs under `npx tsx` from its own package root, so
 *     this file fell outside that config and esbuild used its DEFAULT, which is
 *     the classic runtime: `React.createElement`. There is no `import React`
 *     here, so every render threw `React is not defined`.
 *
 * That failure is invisible to `tsc --noEmit`, to `vitest`, and to both package
 * builds — all four passed. It surfaced only when the `dead-config-ratchet`
 * rendered `form.captcha` for real and recorded an ERRORED probe, which that
 * test correctly refuses to score as a pass. The pragma makes the file state its
 * own runtime so no loader's cwd can decide it.
 *
 * PROVIDER: Google reCAPTCHA v2 ("I'm not a robot").
 *
 * Chosen over Cloudflare Turnstile on 2026-08-16 for a reason that has nothing
 * to do with the technology: Garrett already has a Google account, and Turnstile
 * meant standing up a new vendor for one checkbox on one client's form. Fewer
 * accounts is a real operational win and it is his call to make.
 *
 * The cost is honest and is paid elsewhere: reCAPTCHA sets cookies and reads
 * behavioural signals, which Turnstile does not. So `buildPrivacyPolicy` grew a
 * `captcha` option, every site that turns this on gets a named disclosure, and
 * the "no tracking cookies" sentence was narrowed so the policy stops
 * contradicting itself.
 *
 * V2 CHECKBOX, NOT V3 SCORE. v3 is invisible and returns a 0–1 score, and you
 * choose the threshold that separates human from bot. On a single low-traffic
 * contact page there is no data to choose it from, and getting it wrong drops
 * real enquiries silently — the visitor is thanked, the message is discarded,
 * nobody finds out. That is precisely the failure this whole path exists to
 * refuse. v2 has no threshold: you passed or you did not. It is also what Sam
 * pictured when he said "a recaptcha".
 *
 * Moving to v3 later is small — a different script URL, `execute()` instead of a
 * rendered checkbox, and a score threshold on the server — but it should be a
 * decision with traffic behind it, not a default.
 *
 * WHY A HAND-ROLLED LOADER. The integration is one script tag and one `render()`
 * call. Every React wrapper on npm is bigger than the thing it wraps, and this
 * file has to solve two problems they mostly get wrong anyway: SSR (these sites
 * prerender through `entry-server.tsx`, so `window` is absent at module scope)
 * and loading the script once per document rather than once per widget.
 *
 * WHAT IT DOES NOT DO. It does not decide whether a form is protected. That is
 * read server-side from the published section (`captcha.enabled`), because a
 * check the client can switch off is decoration. This component's only job is to
 * produce a token when asked.
 *
 * WHY IT LIVES HERE. Three renderers now put a captcha on a form:
 * `@gjl/cms-components`' `FormSection` (every CMS-template site), each bespoke
 * CMS site's own markup (Sam Sparrow's `Contact.tsx`), and — since 2026-08-24 —
 * Fairmeadow Capital's contact page, which is not a CMS site at all and has its
 * own Lambda and its own reCAPTCHA registration.
 *
 * It was written here, moved to `@gjl/cms-components` on 2026-08-16, and moved
 * back when that third consumer arrived. The stated reason for the move was
 * that "both packages are siblings — neither depends on the other", and that
 * was simply FALSE when it was written: `@gjl/cms-components` declares
 * `@gjl/site-kit` as a dependency and imports from it (`common/section-address.ts`).
 * So this package could always reach both, and the move bought nothing.
 *
 * What the move was really fixing is worth keeping straight, because it was a
 * real bug and it was not about location: shipping the widget in site-kit had
 * given the bespoke path a captcha while leaving `form.captcha` as a schema key
 * the TEMPLATE renderer ignored. `dead-config-ratchet` caught it within the
 * hour — "a control that lies to the customer just shipped" — and the fix was
 * for `FormSection` to actually mount a widget. It does, and it still does from
 * here; that is what stops the bug returning, not which package holds the file.
 *
 * Living here means Fairmeadow (which already depends on `@gjl/site-kit`, and
 * must never depend on `@gjl/cms-components` — Stripe, patron and the whole CMS
 * tree, for one checkbox) gets the same widget with no new package edge and no
 * second copy. `@gjl/cms-components` re-exports it, so every existing import
 * of `CaptchaWidget` from that package is unchanged.
 */
import { useEffect, useRef, useState } from 'react'

const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit'
const SCRIPT_ID = 'google-recaptcha-script'

interface RecaptchaApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string
      theme?: 'light' | 'dark'
      size?: 'normal' | 'compact'
      callback?: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    }
  ) => number
  reset: (widgetId?: number) => void
}

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi
  }
}

/**
 * Load the reCAPTCHA script once per document, resolving when the API is
 * actually usable.
 *
 * Two traps, both of which produce a widget that silently never appears:
 *
 *   - `window.grecaptcha` is defined BEFORE `grecaptcha.render` is, so a check
 *     for the object alone resolves too early and the next `render()` throws.
 *     Every test here is for the METHOD.
 *   - the `onload` of an already-present tag never fires again, so a second
 *     widget must poll rather than wait on an event it missed. That is what a
 *     naive `if (document.getElementById(...)) return` gets wrong: it returns
 *     immediately and the caller renders against an undefined API.
 */
function loadRecaptcha(): Promise<RecaptchaApi> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA requires a browser'))
  }
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha)

  return new Promise((resolve, reject) => {
    let waited = 0
    const poll = window.setInterval(() => {
      waited += 100
      if (window.grecaptcha?.render) {
        window.clearInterval(poll)
        resolve(window.grecaptcha)
      } else if (waited >= 10_000) {
        window.clearInterval(poll)
        reject(new Error('Timed out waiting for the reCAPTCHA script'))
      }
    }, 100)

    // Already in flight from another widget: the poll above will catch it.
    if (document.getElementById(SCRIPT_ID)) return

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onerror = () => {
      window.clearInterval(poll)
      reject(new Error('Failed to load the reCAPTCHA script'))
    }
    document.head.appendChild(script)
  })
}

export interface CaptchaWidgetProps {
  /** Google's PUBLIC site key. Absent ⇒ renders nothing (see below). */
  siteKey?: string
  /** Called with a fresh token, and with `''` when one expires or errors. */
  onToken: (token: string) => void
  theme?: 'light' | 'dark'
  className?: string
  /** Shown if the script cannot be reached at all. */
  unavailableLabel?: string
}

/**
 * Renders the checkbox and reports its token.
 *
 * ABSENT `siteKey` RENDERS NOTHING, AND THAT IS NOT A SILENT FAILURE — it is the
 * state of every site whose published form has not enabled a captcha, which is
 * most of them. The server-side check is what makes it safe: if a form declares
 * `captcha.enabled` and no widget rendered, no token is sent and the submission
 * is REJECTED rather than quietly accepted. The two halves disagree loudly by
 * construction.
 */
export function CaptchaWidget({
  siteKey,
  onToken,
  theme = 'light',
  className,
  unavailableLabel = 'Verification could not load. Please refresh, or email instead.',
}: CaptchaWidgetProps) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<number | null>(null)
  const [failed, setFailed] = useState(false)

  // `onToken` in a ref so a parent passing an inline arrow — which is every
  // parent — does not tear the widget down and re-render it on each keystroke.
  // Google rate-limits repeated renders of one site key, so this is a
  // correctness concern rather than a performance one.
  const onTokenRef = useRef(onToken)
  useEffect(() => {
    onTokenRef.current = onToken
  }, [onToken])

  useEffect(() => {
    if (!siteKey || !hostRef.current) return
    let cancelled = false
    const host = hostRef.current

    loadRecaptcha()
      .then((api) => {
        if (cancelled) return
        // Guards StrictMode's double-invoked effect from rendering twice into
        // the same node, which reCAPTCHA refuses outright with "reCAPTCHA has
        // already been rendered in this element".
        if (host.childElementCount > 0) return
        widgetIdRef.current = api.render(host, {
          sitekey: siteKey,
          theme,
          callback: (token) => onTokenRef.current(token),
          // A token is single-use and expires after ~2 minutes. Clearing it on
          // expiry is what stops a visitor who paused to write a long message
          // from getting a rejection they cannot explain: the checkbox
          // re-challenges, and the form has no stale token left to send.
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => onTokenRef.current(''),
        })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      const id = widgetIdRef.current
      if (id !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(id)
        } catch {
          // reset() throws if the widget is already gone. Nothing to do about
          // it and nothing worth reporting.
        }
      }
      widgetIdRef.current = null
    }
  }, [siteKey, theme])

  if (!siteKey) return null
  if (failed) {
    return (
      <p role="alert" className={className}>
        {unavailableLabel}
      </p>
    )
  }
  return <div ref={hostRef} className={className} />
}
